import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.esm.js';
import { isElementInChildOfActiveScope } from '@react-aria/focus';
import { useEffect } from 'react';
import { useInteractOutside, useFocusWithin } from 'react-aria';

var visibleOverlays = [];

/**
 * Provides the behavior for overlays such as dialogs, popovers, and menus.
 * Hides the overlay when the user interacts outside it, when the Escape key is pressed,
 * or optionally, on blur. Only the top-most overlay will close at once.
 */
function useOverlay(props, ref) {
  var onClose = props.onClose,
    shouldCloseOnBlur = props.shouldCloseOnBlur,
    isOpen = props.isOpen,
    _props$isDismissable = props.isDismissable,
    isDismissable = _props$isDismissable === void 0 ? false : _props$isDismissable,
    _props$isKeyboardDism = props.isKeyboardDismissDisabled,
    isKeyboardDismissDisabled = _props$isKeyboardDism === void 0 ? false : _props$isKeyboardDism,
    shouldCloseOnInteractOutside = props.shouldCloseOnInteractOutside;

  // Add the overlay ref to the stack of visible overlays on mount, and remove on unmount.
  useEffect(function () {
    if (isOpen) {
      visibleOverlays.push(ref);
    }
    return function () {
      var index = visibleOverlays.indexOf(ref);
      if (index >= 0) {
        visibleOverlays.splice(index, 1);
      }
    };
  }, [isOpen, ref]);

  // Only hide the overlay when it is the topmost visible overlay in the stack
  var onHide = function onHide() {
    if (visibleOverlays[visibleOverlays.length - 1] === ref && onClose) {
      onClose();
    }
  };
  var onInteractOutsideStart = function onInteractOutsideStart(e) {
    if (!shouldCloseOnInteractOutside || shouldCloseOnInteractOutside(e.target)) {
      if (visibleOverlays[visibleOverlays.length - 1] === ref) {
        e.preventDefault();
      }
    }
  };
  var onInteractOutside = function onInteractOutside(e) {
    if (!shouldCloseOnInteractOutside || shouldCloseOnInteractOutside(e.target)) {
      if (visibleOverlays[visibleOverlays.length - 1] === ref) {
        e.preventDefault();
      }
      onHide();
    }
  };

  // Handle the escape key
  var onKeyDown = function onKeyDown(e) {
    if (e.key === 'Escape' && !isKeyboardDismissDisabled && !e.nativeEvent.isComposing) {
      e.stopPropagation();
      e.preventDefault();
      onHide();
    }
  };

  // Handle clicking outside the overlay to close it
  useInteractOutside({
    ref: ref,
    onInteractOutside: isDismissable && isOpen ? onInteractOutside : undefined,
    onInteractOutsideStart: onInteractOutsideStart
  });
  var _useFocusWithin = useFocusWithin({
      isDisabled: !shouldCloseOnBlur,
      onBlurWithin: function onBlurWithin(e) {
        // Do not close if relatedTarget is null, which means focus is lost to the body.
        // That can happen when switching tabs, or due to a VoiceOver/Chrome bug with Control+Option+Arrow navigation.
        // Clicking on the body to close the overlay should already be handled by useInteractOutside.
        // https://github.com/adobe/react-spectrum/issues/4130
        // https://github.com/adobe/react-spectrum/issues/4922
        //
        // If focus is moving into a child focus scope (e.g. menu inside a dialog),
        // do not close the outer overlay. At this point, the active scope should
        // still be the outer overlay, since blur events run before focus.
        if (!e.relatedTarget || isElementInChildOfActiveScope(e.relatedTarget)) {
          return;
        }
        if (!shouldCloseOnInteractOutside || shouldCloseOnInteractOutside(e.relatedTarget)) {
          onClose && onClose();
        }
      }
    }),
    focusWithinProps = _useFocusWithin.focusWithinProps;
  var onPointerDownUnderlay = function onPointerDownUnderlay(e) {
    // fixes a firefox issue that starts text selection https://bugzilla.mozilla.org/show_bug.cgi?id=1675846
    if (e.target === e.currentTarget) {
      e.preventDefault();
    }
  };
  return {
    overlayProps: _objectSpread2({
      onKeyDown: onKeyDown
    }, focusWithinProps),
    underlayProps: {
      onPointerDown: onPointerDownUnderlay
    }
  };
}

export { useOverlay as default };
//# sourceMappingURL=use-overlay.esm.js.map
